import nth from 'lodash/nth'
import { FetchError } from 'ofetch'
import type { _AsyncData } from '#app/composables/asyncData'
import type { StrapiEventListPage } from '~/composables/types/strapiEventListPage'

export const useTransactionalPageResource = async ({
  strapiEventListPageWithNoLocaleFilter,
}: {
  strapiEventListPageWithNoLocaleFilter: _AsyncData<
    StrapiEventListPage[] | null,
    FetchError<any> | null
  >
}): Promise<
  _AsyncData<StrapiEventListPage[] | null, FetchError<any> | null> | undefined
> => {
  const { locale } = useI18n()
  const { fullPath } = useRoute()

  const checkIfNothingFound = (
    asyncData: _AsyncData<StrapiEventListPage[] | null, FetchError<any> | null>
  ) => {
    return (
      asyncData?.error.value ||
      !toRaw(asyncData?.data.value) ||
      !nth(asyncData?.data?.value, 0)
    )
  }

  const redirectToCorrectPageIfAvailable = async (
    entry: StrapiEventListPage,
    currentFullPath: string,
    locale: string
  ) => {
    const strapiEntryForCurrentLocalization =
      strapiEventListPageGetters.getLocalizationByLocale(entry, locale)

    if (strapiEntryForCurrentLocalization) {
      const correctUrl = `/${strapiEventListPageGetters
        .getLocale(strapiEntryForCurrentLocalization)
        ?.toLowerCase()}${strapiEventListPageGetters.getKonfettiPagePath(
        strapiEntryForCurrentLocalization
      )}/`

      if (currentFullPath !== correctUrl) {
        await navigateTo({ path: correctUrl }, { redirectCode: 301 })
        return true
      }
    }

    return false
  }

  if (checkIfNothingFound(strapiEventListPageWithNoLocaleFilter)) {
    return undefined
  }

  // This takes in consideration that we have less than 10 languages
  // Which means that the paginated results will always include
  // The current translation
  //
  // Past 10 languages, this code would not find some of them
  const firstEntryThatMatchesCurrentLocale =
    strapiEventListPageWithNoLocaleFilter.data.value!.find(
      (entry) => entry.attributes.locale.toLowerCase() === locale.value
    )

  const firstEntry =
    firstEntryThatMatchesCurrentLocale ??
    (nth(
      strapiEventListPageWithNoLocaleFilter.data.value,
      0
    ) as StrapiEventListPage)

  const strapiEntryLocale = strapiEventListPageGetters.getLocale(firstEntry)
  if (strapiEntryLocale.toLowerCase() === locale.value) {
    return strapiEventListPageWithNoLocaleFilter
  }

  await redirectToCorrectPageIfAvailable(firstEntry, fullPath, locale.value)

  // This will run even after the navigateTo from the redirectToCorrectPageIfAvailable
  // Which would lead to a bug, but since we are returning the value before redirecting
  // This is a problem on Nuxt 3: https://github.com/nuxt/nuxt/issues/23698
  return strapiEventListPageWithNoLocaleFilter
}
